import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import Sheet from 'components/sheet/Sheet';
import DataTable from 'components/dataTable/DataTable';
import { Badge, IBadgeProps } from 'components/badge/Badge';
import { ButtonNew } from 'components/buttonNew';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlus, faSearch } from '@fortawesome/pro-regular-svg-icons';
import Input from 'components/form/input/Input';
import { showError, showSuccess } from 'redux/actions/ui';
import { useDispatch } from 'react-redux';
import { downloadCsv } from 'helpers/Helpers';
import { GetAudienceCustomers, GetAudienceCustomersExport, IAudience, IAudienceCustomer, PostAudienceCustomers, PostAudienceSubscribeMarketing, PostAudienceUnsubscribeMarketing, PostToggleExclude } from 'api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns';
import { StatusCode } from 'api/protocols';
import Callout from 'components/callout/Callout';
import useDebounce from 'hooks/events/useDebounce';
import _ from 'lodash';
import SelectCustomers from 'components/selectCustomers/SelectCustomers';
import { ICustomer } from 'redux/reducers/models/customer';
import Checkbox from 'components/form/checkbox/Checkbox';
import Spin from 'components/spin/spin';
import { CancelToken } from 'axios';
import useCancelToken from 'hooks/useCancelToken/useCancelToken';
import Popup from 'components/popup/Popup';
import { NavigationDropdownNew } from 'components/navigationDropdownNew/NavigationDropdownNew';

interface IProps {
  audience: IAudience;
  onImport?: () => void;
  onSave?: () => Promise<boolean>;
  selectable?: boolean;
  showAudienceTitle?: boolean;
}
export default function AudienceCustomersTable({
  audience,
  onImport,
  onSave,
  selectable = false,
  showAudienceTitle = false,
}: IProps) {
  const dispatch = useDispatch();
  const { cancelToken, cancelRequest } = useCancelToken();

  const [openCustomerImport, setOpenCustomerImport] = useState<boolean>(false);
  const [openSubscribeAll, setOpenSubscribeAll] = useState<boolean>(false);
  const [openUnsubscribeAll, setOpenUnsubscribeAll] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(
    search,
    200,
  );
  const [selectedFile, setSelectedFile] = useState<File>(null);
  const fileUploadRef = useRef(null);
  const [openAddCustomer, setOpenAddCustomer] = useState(false);
  const limit = 15;
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const [customers, setCustomers] = useState<IAudienceCustomer[]>([]);

  const isStatic = useMemo(() => audience.type === 'static', [audience.type]);

  function getImportTemplate() {
    const csv = "first_name,last_name,email,phone,username,preference_marketing_email\n"
      + "First,Last,email@example.com,,,true\n"
      + "First,Last,email@example.com,,,false";
    downloadCsv(csv, 'customer_import.csv');
  }

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;
    if (file && file.length > 0) {
      setSelectedFile(file[0]);
    }
  }

  async function importCustomerCsv() {
    const formData = new FormData();
    formData.append('csv', selectedFile);
    formData.append('id', String(audience.id));

    const resp = await PostAudienceCustomers(formData, true);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    setOpenCustomerImport(false);
    dispatch(showSuccess(t('customers imported')));

    onImport?.();
    cancelRequest();
    void loadAudienceCustomers(cancelToken());
  }

  function handleCustomerSearch(search: string) {
    setSearch(search);
  }

  function badgeStatus(customer: IAudienceCustomer): IBadgeProps['type'] {
    if (customer.status === 'unsubscribed') {
      return 'error';
    }

    if (customer.excluded || customer.status === 'excluded') {
      return 'warning';
    }

    if (customer.status === 'active') {
      return 'success';
    }


    return 'gray';
  }

  async function handleAddCustomer(addedCustomers: ICustomer[]) {
    if (addedCustomers.length === 0) {
      return;
    }

    for (const customer of addedCustomers) {
      const resp = await PostToggleExclude({
        id: audience.id,
        customer_id: customer.id,
        excluded: false,
      }, true);

      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message));
        return;
      }
    }

    setOpenAddCustomer(false);
    setSearch('');
    setOffset(0);
    dispatch(showSuccess(t('Successfully added new customer')));
    onImport?.();
    cancelRequest();
    void loadAudienceCustomers(cancelToken());
  }

  async function onExport() {
    const resp = await GetAudienceCustomersExport({ id: audience.id });
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(t('Failed to export customers')));
      return;
    }
    downloadCsv(resp.data, _.snakeCase(audience.title) + '.csv');

  }

  async function subscribeAllCustomers() {
    if (onSave) {
      const saved = await onSave();
      if (!saved) {
        return;
      }
    }

    const resp = await PostAudienceSubscribeMarketing({ id: audience.id }, true);
    const message = String(resp.message ?? '');

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(message || t('Failed to subscribe customers')));
      return;
    }

    setOpenSubscribeAll(false);
    dispatch(showSuccess(message || t('Subscribed audience customers to marketing emails')));
    onImport?.();
    cancelRequest();
    void loadAudienceCustomers(cancelToken());
  }

  async function unsubscribeAllCustomers() {
    if (onSave) {
      const saved = await onSave();
      if (!saved) {
        return;
      }
    }

    const resp = await PostAudienceUnsubscribeMarketing({ id: audience.id }, true);
    const message = String(resp.message ?? '');

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(message || t('Failed to unsubscribe customers')));
      return;
    }

    setOpenUnsubscribeAll(false);
    dispatch(showSuccess(message || t('Unsubscribed audience customers from marketing emails')));
    onImport?.();
    cancelRequest();
    void loadAudienceCustomers(cancelToken());
  }

  function onPageChange(dir: 'next' | 'prev', limit: number, offset: number) {
    if (dir === 'next') {
      setOffset(offset + limit);
    } else {
      setOffset(offset - limit);
    }
  }

  async function loadAudienceCustomers(token: CancelToken) {
    if (audience.id == 0) {
      return;
    }

    const page = Math.floor(offset / limit) + 1;

    setLoading(true);
    const resp = await GetAudienceCustomers(
      {
        id: audience.id,
        page,
        search: search,
        filters: audience.filters,
      },
      false,
      token,
    );
    setLoading(false);

    if (resp.status !== StatusCode.OK) {
      if (token && token.reason) {
        return;
      }
      dispatch(showError(resp.message));
      return;
    }

    setCustomers(resp.data.customers.data);
  }

  useEffect(() => {
    if (audience.id == 0) {
      return;
    }

    cancelRequest();
    void loadAudienceCustomers(cancelToken());
  }, [offset, debouncedSearch, audience.filters]);

  async function onIncludeToggle(customer: IAudienceCustomer): Promise<void> {
    const resp = await PostToggleExclude({ id: audience.id, customer_id: customer.id, excluded: !customer.excluded });
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    onImport?.();
    cancelRequest();
    void loadAudienceCustomers(cancelToken());
  }

  const showImport = onImport != null;
  const showBulkPreferenceActions = audience.id > 0;

  return <>
    <div className="text-lg font-semibold flex gap-2 items-center mb-2">
      {showAudienceTitle ? audience.title : t(`${isStatic ? 'Static' : 'Smart'} List Customers`)}
      <Badge>{audience.included_customer_count} of {audience.total_customer_count} {t('Selected')}</Badge>
      <div className="grow"></div>
      <NavigationDropdownNew
        rightAlign
        type="secondary"
        label={t('Actions')}
        sections={[
          [
            {
              label: t('Import'),
              icon: 'circle-arrow-down',
              hidden: !showImport,
              onClick: () => setOpenCustomerImport(true),
            },
            {
              label: t('Export'),
              icon: 'circle-arrow-up',
              onClick: () => void onExport(),
            },
            {
              label: t('Opt-in customers'),
              icon: 'envelope',
              hidden: !showBulkPreferenceActions,
              onClick: () => setOpenSubscribeAll(true),
            },
            {
              label: t('Opt-out customers'),
              icon: 'user-xmark',
              hidden: !showBulkPreferenceActions,
              onClick: () => setOpenUnsubscribeAll(true),
            },
          ],
        ]}
      />
      <Input
        placeholder={t("Search name or email")}
        value={search}
        onChange={e => handleCustomerSearch(e.target.value)}
        prefix={<FontAwesomeIcon icon={faSearch} />}
        labelHidden={true}
      />
    </div>

    <div className="overflow-x-auto **:[.ui-card,.data-table]:!overflow-visible">
      <DataTable
        columns={[
          { label: t('Name') },
          { label: t('Email address') },
          { label: t('Status') },
        ]}
        footer={{
          tableLimit: limit,
          tableOffset: offset,
          handleTableOffset: dir => onPageChange(dir, limit, offset),
          disableNextOffset: (offset + limit) > audience.total_customer_count,
        }}
      >
      {loading && <tr>
        <td colSpan={5} className="text-center">
          <div className="h-8 flex justify-center gap-4 items-center">
            {t('Loading...')}
            <Spin />
          </div>
        </td>
      </tr>}
      {!loading && customers.length === 0 && <tr>
        <td colSpan={5} className="text-center">
          {t('No customers found with current filters.')}
          {onImport && <div className="flex justify-center items-center w-full mt-2">
            <ButtonNew type="primary" size="small" onClick={() => setOpenAddCustomer(true)}>
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              {t('Add Customer')}
            </ButtonNew>
          </div>}
        </td>
      </tr>}
      {!loading && customers.map((customer, i) =>
        <tr key={customer.id.toString() + '-' + customer.status + '-' + i}>
          <td onClick={() => selectable && customer.status !== 'unsubscribed' && onIncludeToggle(customer)} className="cursor-pointer">

            <div className="flex items-center">
              {selectable && customer.status !== 'unsubscribed' ? <Checkbox
                onChange={() => onIncludeToggle(customer)}
                size="small"
                readOnly={true}
                disabled={!selectable}
                checked={!customer.excluded}
              /> : <div style={{ width: selectable ? '24px' : 0 }}></div>}

              {customer.first_name} {customer.last_name}
            </div>
          </td>
          <td>{customer.email}</td>
          <td><Badge type={badgeStatus(customer)}>{_.startCase(customer.excluded ? t('Excluded') : customer.status ?? 'undef')}</Badge></td>
        </tr>,
      )}
      </DataTable>
    </div>

    <Sheet
      open={openCustomerImport}
      okText={t('Import')}
      onOk={importCustomerCsv}
      onSecondary={getImportTemplate}
      onCancel={() => setOpenCustomerImport(false)}
      secondaryText={<div><FontAwesomeIcon icon={faDownload} /> {t('CSV Template')}</div>}
      title={t('Import Customer List')}
      okDisabled={selectedFile == null}
    >
      <div className="text-md mb-4">
        <div>{t('Import a static list of customer to receive email campaigns.')}</div>
        <div>{t('New customers will be created if they do not exist.')}</div>
      </div>

      <Callout
        type="info"
        title={!selectedFile ? t("No file selected") : t("Selected File")}
        content={!selectedFile ? t("Select a file to continue") : selectedFile.name}
      />

      <div className="file-import-container my-2">
        <input
          ref={fileUploadRef}
          className="file-import-input"
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
        />
        <FontAwesomeIcon className="file-import-icon" icon={["far", "upload"]} />
      </div>
    </Sheet>

    <SelectCustomers
      open={openAddCustomer}
      onOk={customers => void handleAddCustomer(customers)}
      onClose={() => setOpenAddCustomer(false)}
    />

    <Popup
      open={openSubscribeAll}
      type="warning"
      title={t('Opt-in customers')}
      description={t(
        onSave
          ? 'This will first save the current list, then subscribe all customers in it to marketing emails. By continuing, you acknowledge that you have obtained consent from these customers to change their notification preferences.'
          : 'By continuing, you acknowledge that you have obtained consent from these customers to change their notification preferences. This will subscribe all customers in this list to marketing emails.',
      )}
      okText={t('I Acknowledge')}
      onOk={subscribeAllCustomers}
      onCancel={() => setOpenSubscribeAll(false)}
    />

    <Popup
      open={openUnsubscribeAll}
      type="warning"
      title={t('Opt-out customers')}
      description={t(
        onSave
          ? 'This will first save the current list, then unsubscribe all customers in it from marketing emails. By continuing, you acknowledge that you have obtained consent from these customers to change their notification preferences.'
          : 'By continuing, you acknowledge that you have obtained consent from these customers to change their notification preferences. This will unsubscribe all customers in this list from marketing emails.',
      )}
      okText={t('I Acknowledge')}
      onOk={unsubscribeAllCustomers}
      onCancel={() => setOpenUnsubscribeAll(false)}
    />
  </>;
}
